import { http } from "../http";
import type {
  DashboardAnalyticsResponse,
  TopFiveAnalyticsResponse,
  PaymentAnalyticsResponse,
  PayoutsResponse,
  PayoutResponse,
  PayoutStatisticsResponse,
  ClinicianPayoutStatisticsResponse,
  SessionsResponse,
  SessionResponse,
  RefundStatusResponse,
  PatientsResponse,
  PatientResponse,
  PatientProfileResponse,
  PatientDashboardResponse,
  CliniciansResponse,
  ClinicianResponse,
  ClinicianDashboardResponse,
  ReviewsResponse,
  ReportsResponse,
  AdminReportsResponse,
  MedicalRecordsResponse,
  MedicalRecordResponse,
  TimeSlotsResponse,
  AllPricingResponse,
  PricingStatsResponse,
  ClinicianPricingResponse,
  ServicesResponse,
  ServiceResponse,
  ConsultationModesResponse,
  ConsultationModeResponse,
  SymptomsResponse,
  SymptomResponse,
  RolesResponse,
  RoleResponse,
  UsersResponse,
  UserResponse,
  PermissionsResponse,
  PermissionResponse,
  NotificationsResponse,
  ResourcesResponse,
  SystemSettingsResponse,
  ApiResponse,
} from "../types";

// Helper function to unwrap extra data layer
const unwrapResponse = <T,>(response: any): T => {
  // If response.data.data exists, unwrap it by returning just the data
  if (
    response?.data &&
    typeof response.data === "object" &&
    "data" in response.data
  ) {
    // Return a new response object with only the essential properties
    return {
      data: response.data.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: response.config,
    } as T;
  }
  return response as T;
};

// Dashboard Analytics
export const getDashboardAnalytics =
  async (): Promise<DashboardAnalyticsResponse> => {
    const response = await http.get(`/admin/analytics`);
    return unwrapResponse(response);
  };

export const getTopFiveAnalytics =
  async (): Promise<TopFiveAnalyticsResponse> => {
    const response = await http.get(`/admin/top-five`);
    return unwrapResponse(response);
  };

// Payment & Payout Analytics
export const paymentAnalytics = async (): Promise<PaymentAnalyticsResponse> => {
  const response = await http.get(`/dashboard/payments`);
  return unwrapResponse(response);
};

export const getTopChargedClinicians = async (): Promise<ApiResponse<any>> => {
  const response = await http.get(`/dashboard/payments/top-five`);
  return unwrapResponse(response);
};

export const getPayoutAnalytics =
  async (): Promise<PaymentAnalyticsResponse> => {
    const response = await http.get(`/payouts/admin/payment-analytics`);
    return unwrapResponse(response);
  };

export const getSessions = async ({
  queryKey,
}: any): Promise<SessionsResponse> => {
  const response = await http.get("/sessions", {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getSessionById = async ({
  queryKey,
}: any): Promise<SessionResponse> => {
  const response = await http.get(`/sessions/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const getSessionMeetingInfo = async ({
  queryKey,
}: any): Promise<ApiResponse<any>> => {
  const response = await http.get(`/sessions/${queryKey[1]}/meeting-info`);
  return unwrapResponse(response);
};

export const createSessionCalendarEvent = async (
  sessionId: number
): Promise<ApiResponse<any>> => {
  const response = await http.post(`/sessions/${sessionId}/calendar-event`);
  return unwrapResponse(response);
};

export const getSessionAttendance = async ({
  queryKey,
}: any): Promise<ApiResponse<any>> => {
  const response = await http.get(`/sessions/${queryKey[1]}/attendance`);
  return unwrapResponse(response);
};

export const refreshSessionAttendance = async (
  sessionId: number
): Promise<ApiResponse<any>> => {
  const response = await http.post(`/sessions/${sessionId}/attendance/refresh`);
  return unwrapResponse(response);
};

export const getSessionRecordings = async ({
  queryKey,
}: any): Promise<ApiResponse<any>> => {
  const response = await http.get(`/sessions/${queryKey[1]}/recordings`);
  return unwrapResponse(response);
};

export const refreshSessionRecordings = async (
  sessionId: number
): Promise<ApiResponse<any>> => {
  const response = await http.post(`/sessions/${sessionId}/recordings/refresh`);
  return unwrapResponse(response);
};

export function updateClinicianSession({
  id,
  body,
}: any): Promise<SessionResponse> {
  return http.patch(`/sessions/assign-new-clinician/${id}`, body);
}

// Refund Management
export const processRefund = async ({
  sessionId,
  body,
}: any): Promise<RefundStatusResponse> => {
  const response = await http.post(
    `/sessions/refunds/process/${sessionId}`,
    body
  );
  return unwrapResponse(response);
};

export const getRefundStatus = async ({
  queryKey,
}: any): Promise<RefundStatusResponse> => {
  const response = await http.get(`/sessions/refunds/status/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const getPatients = async ({
  queryKey,
}: any): Promise<PatientsResponse> => {
  const response = await http.get("/patients", {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getClinicians = async ({
  queryKey,
}: any): Promise<CliniciansResponse> => {
  const response = await http.get("/admin/clinicians", {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export async function getAvailableClinicians({
  queryKey,
}: any): Promise<CliniciansResponse> {
  const response = await http.get("/sessions/available-clinicians", {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const getClinicianProfile = async (): Promise<ClinicianResponse> => {
  const response = await http.get("/clinicians/profile");
  return unwrapResponse(response);
};

export const getClinicianProfileById = async ({
  queryKey,
}: any): Promise<ClinicianResponse> => {
  const response = await http.get(`/admin/clinicians/${queryKey[1]}/profile`);
  return unwrapResponse(response);
};

export const getClinicianPayouts = async ({
  queryKey,
}: any): Promise<PayoutsResponse> => {
  const response = await http.get("/payouts", {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getPatientById = async ({
  queryKey,
}: any): Promise<PatientProfileResponse> => {
  const response = await http.get(`/patients/patient-profiles/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const getPayoutStatistics = async ({
  queryKey,
}: any): Promise<ClinicianPayoutStatisticsResponse> => {
  const response = await http.get(`/payouts/clinicians/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const getPayoutAmount = async ({
  queryKey,
}: any): Promise<ApiResponse<{ pendingAmount: number }>> => {
  const response = await http.get(`/payouts/clinicians/${queryKey[1]}/pending`);
  return unwrapResponse(response);
};

export const getClinicianDashboardData = async ({
  queryKey,
}: any): Promise<ClinicianDashboardResponse> => {
  const response = await http.get(`/dashboard/clinicians/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const getClinicianPatients = async ({
  queryKey,
}: any): Promise<PatientsResponse> => {
  const response = await http.get(`/clinicians/${queryKey[1]}/patients`, {
    params: {
      ...queryKey[2],
    },
  });
  return unwrapResponse(response);
};

export const getReviews = async ({
  queryKey,
}: any): Promise<ReviewsResponse> => {
  const response = await http.get(`/reviews`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getReports = async ({
  queryKey,
}: any): Promise<ReportsResponse> => {
  const response = await http.get(`/reports`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getMedicalRecords = async ({
  queryKey,
}: any): Promise<MedicalRecordsResponse> => {
  const response = await http.get(`/medical-records`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getReportsById = async ({
  queryKey,
}: any): Promise<ApiResponse<any>> => {
  const sessionId = queryKey[1];
  const userType = queryKey[2] ?? "CLINICIAN";
  const response = await http.get(`/reports/${sessionId}`, {
    params: { userType },
  });
  return unwrapResponse(response);
};

export const getClinicianTimeSlots = async ({
  queryKey,
}: any): Promise<TimeSlotsResponse> => {
  const response = await http.get(
    `clinician-time-slot/clinicians/${queryKey[1]}/slots`
  );
  return unwrapResponse(response);
};

export const updateTimeSlots = async (
  body: any
): Promise<TimeSlotsResponse> => {
  const response = await http.patch("clinician-time-slot", body);
  return unwrapResponse(response);
};

export const createPayouts = async (body: any): Promise<PayoutResponse> => {
  const response = await http.post("payouts", body);
  return unwrapResponse(response);
};

export const getAllPayouts = async ({
  queryKey,
}: any): Promise<PayoutsResponse> => {
  const response = await http.get("payouts", {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getPayoutById = async ({
  queryKey,
}: any): Promise<PayoutResponse> => {
  const response = await http.get(`/payouts/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const updatePayout = async ({
  data,
  id,
}: any): Promise<PayoutResponse> => {
  const response = await http.patch(`payouts/${id}`, data);
  return unwrapResponse(response);
};

export const deletePayout = async (id: any): Promise<ApiResponse<void>> => {
  const response = await http.delete(`payouts/${id}`);
  return unwrapResponse(response);
};

export const getPatientDasboardData = async ({
  queryKey,
}: any): Promise<PatientDashboardResponse> => {
  const response = await http.get(`/dashboard/patients/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const payoutSats = async (): Promise<PayoutStatisticsResponse> => {
  const response = await http.get(`/payouts/statistics`);
  return unwrapResponse(response);
};

export const getAdminReports = async ({
  queryKey,
}: any): Promise<AdminReportsResponse> => {
  const response = await http.get(`/admin/reports`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export async function getAllResources({
  queryKey,
}: any): Promise<ResourcesResponse> {
  const response = await http.get(`/resources/`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const createServices = (body: any): Promise<ServiceResponse> => {
  return http.post(`/services/`, body);
};

export async function getAllServices({
  queryKey,
}: any): Promise<ServicesResponse> {
  const response = await http.get(`/services/`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const updateServices = ({ body, id }: any): Promise<ServiceResponse> => {
  return http.patch(`/services/${id}`, body);
};

export const createConsultationMode = (
  body: any
): Promise<ConsultationModeResponse> => {
  return http.post(`/mode-of-consultation/`, body);
};

export async function getAllConsultationModes({
  queryKey,
}: any): Promise<ConsultationModesResponse> {
  const response = await http.get(`/mode-of-consultation/`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const updateConsultationMode = ({
  body,
  id,
}: any): Promise<ConsultationModeResponse> => {
  return http.patch(`/mode-of-consultation/${id}`, body);
};

export const deleteConsultationMode = (id: any): Promise<ApiResponse<void>> => {
  return http.delete(`/mode-of-consultation/${id}`);
};

export async function getAllSymptoms({
  queryKey,
}: any): Promise<SymptomsResponse> {
  const response = await http.get(`/symptoms/`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const deleteServices = (id: any): Promise<ApiResponse<void>> => {
  return http.delete(`/services/${id}`);
};

export const createSymptoms = (body: any): Promise<SymptomResponse> => {
  return http.post(`/symptoms`, body);
};

export const updateSymptoms = ({ body, id }: any): Promise<SymptomResponse> => {
  return http.patch(`/symptoms/${id}`, body);
};

export const deleteSymptom = (id: any): Promise<ApiResponse<void>> => {
  return http.delete(`/symptoms/${id}`);
};

// roles
export const createRole = (body: any): Promise<RoleResponse> => {
  return http.post("/roles", body);
};

export async function getAllRoles({ queryKey }: any): Promise<RolesResponse> {
  const response = await http.get(`/roles`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const getOneRole = ({ body, id }: any): Promise<RoleResponse> => {
  return http.patch(`/roles/${id}`, body);
};

export const updateRole = ({ body, id }: any): Promise<RoleResponse> => {
  return http.patch(`/roles/${id}`, body);
};

export const deleteRole = (id: any): Promise<ApiResponse<void>> => {
  return http.delete(`/roles/${id}`);
};

//users
export const createUser = (body: any): Promise<UserResponse> => {
  return http.post("/users/admin-users", body);
};

export async function getAllUsers({ queryKey }: any): Promise<UsersResponse> {
  const response = await http.get(`/users/admin-users`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

// export const getOneUser = ({ body, id }: any) => {
//   return http.patch(`/Users/${id}`, body);
// };

export const updateUser = ({ body, id }: any): Promise<UserResponse> => {
  return http.put(`/users/admin-users/${id}`, body);
};

export const deleteUser = (id: any): Promise<ApiResponse<void>> => {
  return http.delete(`/users/admin-users/${id}`);
};

// Permissions
export const createPermission = (body: any): Promise<PermissionResponse> => {
  return http.post("/permission", body);
};

export async function getAllPermissions({
  queryKey,
}: any): Promise<PermissionsResponse> {
  const response = await http.get(`/permission`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const getOnePermission = (id: any): Promise<PermissionResponse> => {
  return http.get(`/permission/${id}`);
};

export const updatePermission = ({
  body,
  id,
}: any): Promise<PermissionResponse> => {
  return http.patch(`/permission/${id}`, body);
};

export const deletePermission = (id: any): Promise<ApiResponse<void>> => {
  return http.delete(`/permission/${id}`);
};

// Pricing Management
export const createPricing = (body: any): Promise<ApiResponse<any>> => {
  return http.post("/admin/pricing", body);
};

export const bulkUpdatePricing = (body: any): Promise<ApiResponse<any>> => {
  return http.post("/admin/pricing/bulk", body);
};

export async function getAllPricing({
  queryKey,
}: any): Promise<AllPricingResponse> {
  const response = await http.get(`/admin/pricing`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const getPricingStats = async (): Promise<PricingStatsResponse> => {
  const response = await http.get(`/admin/pricing/stats`);
  return unwrapResponse(response);
};

export const getClinicianPricing = async ({
  queryKey,
}: any): Promise<ClinicianPricingResponse> => {
  const response = await http.get(`/admin/pricing/clinician/${queryKey[1]}`);
  return unwrapResponse(response);
};

export const updatePricing = ({ body, id }: any): Promise<ApiResponse<any>> => {
  return http.put(`/admin/pricing/${id}`, body);
};

export const deletePricing = (id: any): Promise<ApiResponse<void>> => {
  return http.delete(`/admin/pricing/${id}`);
};

export const setDefaultPricing = (body: any): Promise<ApiResponse<any>> => {
  return http.post("/admin/pricing/global/defaults", body);
};

export const copyPricing = (body: any): Promise<ApiResponse<any>> => {
  return http.post("/admin/pricing/copy", body);
};

export const getClinicianCommission = async ({
  queryKey,
}: any): Promise<{
  data?: {
    clinician_id?: number;
    clinicianName?: string;
    clinicianEmail?: string;
    commission?: number;
    sampleBreakdown?: {
      sampleAmount: number;
      clinicianShare: number;
      platformShare: number;
      note?: string;
    };
  };
  status?: string;
  message?: string;
}> => {
  const response = await http.get(`/admin/clinicians/${queryKey[1]}/commission`);
  return unwrapResponse(response);
};

export const updateClinicianCommission = (
  clinicianId: number,
  commission: number
): Promise<{
  data?: {
    message?: string;
    clinician_id?: number;
    newCommission?: number;
  };
  status?: string;
  message?: string;
}> => {
  return http
    .patch(`/admin/clinicians/${clinicianId}/commission`, {
      commission,
    })
    .then((response) => unwrapResponse(response));
};

// ============================================================================
// NOTIFICATIONS MANAGEMENT
// ============================================================================

export async function getAllNotifications({
  queryKey,
}: any): Promise<NotificationsResponse> {
  const response = await http.get(`/notifications/admin/all`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

// Medical Records
export async function getAllMedicalRecords({
  queryKey,
}: any): Promise<MedicalRecordsResponse> {
  const response = await http.get(`/medical-records/admin/all`, {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
}

export const getMedicalRecordById = async ({
  queryKey,
}: any): Promise<MedicalRecordResponse> => {
  const response = await http.get(`/medical-records/${queryKey[1]}`);
  return unwrapResponse(response);
};

// Clinician Management (Admin functions)
export const approveClinicianProfile = (
  id: any
): Promise<ClinicianResponse> => {
  return http.patch(`/clinicians/profile/${id}`, { isApproved: true });
};

export const updateClinicianStatus = ({
  id,
  status,
}: any): Promise<ClinicianResponse> => {
  return http.patch(`/clinicians/profile/${id}`, { status });
};

// Patient Management (Admin functions)
export const createPatient = (body: any): Promise<PatientResponse> => {
  return http.post(`/patients`, body);
};

export const inviteClinician = (body: any) => {
  return http.post(`/users/clinician-invite`, body);
};

export const resendClinicianInvite = (id: number) => {
  return http.post(`/users/clinician-invite/${id}/resend`);
};

export const deleteClinician = (id: number) => {
  return http.delete(`/admin/clinicians/${id}`);
};

export const getClinicianDeleteEligibility = async (id: number) => {
  const response = await http.get(`/admin/clinicians/${id}/delete-eligibility`);
  return unwrapResponse(response);
};

export const updatePatient = ({
  id,
  body,
}: {
  id: number;
  body: any;
}): Promise<PatientResponse> => {
  return http.patch(`/patients/${id}`, body);
};

export const updatePatientStatus = ({
  id,
  status,
}: any): Promise<PatientResponse> => {
  return http.patch(`/patients/${id}`, { status });
};

// Audit Logs / Activity Logs (Not implemented on server yet)
// export function getAuditLogs({ queryKey }: any) {
//   return http.get(`/audit-logs`, {
//     params: {
//       ...queryKey[1],
//     },
//   });
// }

// System Settings
export function getSystemSettings(): Promise<SystemSettingsResponse> {
  return http.get(`/settings`);
}

export const updateSystemSettings = (
  body: any
): Promise<SystemSettingsResponse> => {
  return http.patch(`/settings`, body);
};

// Specialist Registrations
export const getSpecialistRegistrations = async ({
  queryKey,
}: any): Promise<ApiResponse<any>> => {
  const response = await http.get("/specialist-registrations", {
    params: {
      ...queryKey[1],
    },
  });
  return unwrapResponse(response);
};

export const getSpecialistRegistrationById = async ({
  queryKey,
}: any): Promise<ApiResponse<any>> => {
  const response = await http.get(
    `/specialist-registrations/${queryKey[1]}`
  );
  return unwrapResponse(response);
};

export const getSpecialistRegistrationStats =
  async (): Promise<ApiResponse<any>> => {
    const response = await http.get("/specialist-registrations/stats");
    return unwrapResponse(response);
  };

export const createSpecialistRegistration = async (
  data: any
): Promise<ApiResponse<any>> => {
  const response = await http.post("/specialist-registrations", data);
  return unwrapResponse(response);
};

export const updateSpecialistRegistrationStatus = async ({
  id,
  status,
  review_notes,
}: {
  id: number;
  status: string;
  review_notes?: string;
}): Promise<ApiResponse<any>> => {
  const response = await http.patch(
    `/specialist-registrations/${id}/status`,
    {
      status,
      review_notes,
    }
  );
  return unwrapResponse(response);
};
